<template>
  <b-card class="presales-daily-chart" v-if="hasRight(user, 'dashboard', 'view')" no-body>
    <template #header>
      <h6 class="mb-0">Daily Activity (Presales)</h6>
    </template>
    <b-card-body>
      <apexchart type="bar" :options="chartOptions" :series="safeSeries"></apexchart>
    </b-card-body>
  </b-card>
</template>

<script>
import VueApexCharts from 'vue-apexcharts';
import { BCard, BCardBody } from 'bootstrap-vue';
import { hasRight } from '@/utils/auth';
import { dashboardGet } from './dashboardApi';

export default {
  name: 'PresalesDailyChart',
  components: {
    BCard,
    BCardBody,
    apexchart: VueApexCharts,
  },
  props: {
    filters: {
      type: Object,
      required: true,
    },
    user: {
      type: Object,
      default: null,
    },
    dashboardData: { type: Object, default: null },
    useAggregate: { type: Boolean, default: false },
    baseApi: {
      type: String,
      default: () => process.env.VUE_APP_APIENDPOINT || '',
    },
    accessToken: {
      type: String,
      default: () => localStorage.getItem('accessToken') || '',
    },
  },
  data() {
    return {
      chartOptions: {
        chart: {
          type: 'bar',
          stacked: true,
          toolbar: {
            show: false,
          },
        },
        responsive: [
          {
            breakpoint: 480,
            options: {
              legend: {
                position: 'bottom',
                offsetX: -10,
                offsetY: 0,
              },
            },
          },
        ],
        xaxis: {
          categories: [], // Dates will go here
        },
        yaxis: {
          title: {
            text: 'Count',
          },
        },
        fill: {
          opacity: 1,
        },
        legend: {
          position: 'right',
          offsetX: 0,
          offsetY: 50,
        },
      },
      series: [],
    };
  },
  computed: {
    safeSeries() {
      const s = this.series || [];
      const mapped = s.map((item) => ({ name: item.name || '', data: Array.isArray(item.data) ? item.data : [] }));
      return mapped.length ? mapped : [{ name: '', data: [] }];
    },
  },
  watch: {
    dashboardData: { deep: true, handler(v) { if (v && v.presales_daily_chart != null && hasRight(this.user, 'dashboard', 'view')) this.processChartData(v.presales_daily_chart); } },
    filters: {
      deep: true,
      immediate: true,
      handler() {
        if (!hasRight(this.user, 'dashboard', 'view')) return;
        if (this.useAggregate) {
          if (this.dashboardData && this.dashboardData.presales_daily_chart != null) this.processChartData(this.dashboardData.presales_daily_chart);
          return;
        }
        if (this.dashboardData && this.dashboardData.presales_daily_chart != null) { this.processChartData(this.dashboardData.presales_daily_chart); return; }
        this.fetchPresalesDailyChartData();
      },
    },
    user: {
      deep: true,
      immediate: true,
      handler(newVal) {
        if (hasRight(newVal, 'dashboard', 'view')) {
          if (this.useAggregate) {
            if (this.dashboardData && this.dashboardData.presales_daily_chart != null) this.processChartData(this.dashboardData.presales_daily_chart);
            return;
          }
          this.fetchPresalesDailyChartData();
        } else {
          this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
          this.series = [];
        }
      },
    },
  },
  methods: {
    hasRight, // Make the helper available in the template
    async fetchPresalesDailyChartData() {
      if (!hasRight(this.user, 'dashboard', 'view')) return;
      if (this.useAggregate || (this.dashboardData && this.dashboardData.presales_daily_chart != null)) {
        if (this.dashboardData && this.dashboardData.presales_daily_chart != null) this.processChartData(this.dashboardData.presales_daily_chart);
        return;
      }
      try {
        const response = await dashboardGet(this.baseApi, this.accessToken, '/dashboard/crm/presales-daily-chart', this.filters);
        const raw = response.data && response.data.data;
        this.processChartData(Array.isArray(raw) ? raw : []);
      } catch (error) {
        console.error('Error fetching presales daily chart data:', error);
        // Handle error
      }
    },
    processChartData(data) {
      if (!data || !Array.isArray(data) || data.length === 0) {
        this.series = [];
        this.chartOptions = { ...this.chartOptions, xaxis: { categories: [] } };
        return;
      }
      const categories = [...new Set(data.map(item => item.date))].sort();
      const allTypes = [...new Set(data.map(item => item.followuptype))];

      const seriesMap = {};
      allTypes.forEach(type => {
        seriesMap[type] = new Array(categories.length).fill(0);
      });

      data.forEach(item => {
        const dateIndex = categories.indexOf(item.date);
        if (dateIndex !== -1) {
          seriesMap[item.followuptype][dateIndex] = item.count;
        }
      });

      this.chartOptions = {
        ...this.chartOptions,
        xaxis: {
          categories: categories,
        },
      };

      this.series = allTypes.map(type => ({
        name: type,
        data: seriesMap[type],
      }));
    },
  },
};
</script>

<style scoped>
.presales-daily-chart {
  margin-top: 20px;
}
</style>